import { supabase } from './supabaseClient';
import { careDailyGoalsService, CareDailyGoal } from './careDailyGoalsService';
import { CategoryService } from './categoryService';

/**
 * Resident Goal Interface
 */
export interface ResidentGoal {
  id: string;
  residentId: string;
  facilityId: string;
  title: string;
  description?: string;
  categoryId?: string | null;
  completed: boolean;
  completedAt?: string | null;
  createdAt: string;
  updatedAt: string;
  careDailyGoalId?: string | null;
  careDailyLocationId?: string | null;
}

/**
 * Goal Creation/Update Input
 */
export interface ResidentGoalInput {
  title: string;
  description?: string;
  categoryId?: string | null;
}

/**
 * Goals Summary
 */
export interface GoalsSummary {
  totalGoals: number;
  completedGoals: number;
  activeGoals: number;
  completionPercentage: number;
}

/**
 * Service for managing resident wellness goals.
 *
 * Source of truth by integration status:
 *  - Linked residents (careDailyLocationId present): CareDaily location state is the
 *    read authority. Goals are fetched directly from CareDaily and mapped in memory.
 *    Supabase is used only as a stale fallback if CareDaily is unreachable.
 *  - Unlinked residents (no careDailyLocationId): Supabase is the sole read/write store.
 *
 * Writes for linked residents go through the ESP goalActivity endpoint (GoalActivityService).
 * Do NOT write goals to CareDaily location state directly from this service.
 */
export class ResidentGoalsService {
  /**
   * Get all goals for a resident.
   *
   * For linked residents (careDailyLocationId provided), fetches directly from CareDaily
   * location state and maps in memory. Falls back to Supabase if CareDaily is unavailable.
   *
   * For unlinked residents, reads from Supabase only.
   */
  static async getGoals(
    residentId: string,
    facilityId: string,
    careDailyLocationId?: string,
    _syncWithCareDaily: boolean = false  // kept for call-site compatibility; ignored for linked residents
  ): Promise<ResidentGoal[]> {
    console.log('🎯 Fetching goals for resident:', residentId);

    if (careDailyLocationId) {
      return this.getGoalsFromCareDaily(residentId, facilityId, careDailyLocationId);
    }

    return this.getGoalsFromSupabase(residentId, facilityId);
  }

  /**
   * Fetch goals directly from CareDaily location state.
   * Falls back to Supabase if CareDaily is unreachable.
   */
  private static async getGoalsFromCareDaily(
    residentId: string,
    facilityId: string,
    careDailyLocationId: string
  ): Promise<ResidentGoal[]> {
    try {
      let categoryIdByKey: Map<string, string> = new Map();
      let categoryIdByDisplayName: Map<string, string> = new Map();
      try {
        const categories = await CategoryService.getCategories(facilityId);
        categoryIdByKey = new Map(
          categories
            .filter(c => !!c.id && !!c.categoryKey)
            .map(c => [c.categoryKey.toLowerCase(), c.id])
        );
        categoryIdByDisplayName = new Map(
          categories
            .filter(c => !!c.id && !!c.displayName)
            .map(c => [c.displayName.toLowerCase(), c.id])
        );
      } catch (e) {
        console.warn('⚠️ Could not load categories for CareDaily goal mapping. category_id will be null.', e);
      }

      const resolveCategoryId = (careDailyCategory?: string): string | null => {
        const raw = (careDailyCategory || '').trim();
        if (!raw) return null;
        const normalized = raw.toLowerCase();
        const byDisplayName = categoryIdByDisplayName.get(normalized);
        if (byDisplayName) return byDisplayName;
        const direct = categoryIdByKey.get(normalized);
        if (direct) return direct;
        const normalizedKey = normalized.replace(/\s+/g, '_').replace(/-+/g, '_');
        return categoryIdByKey.get(normalizedKey) ?? null;
      };

      await careDailyGoalsService.initialize(facilityId);
      const allGoals = await careDailyGoalsService.getGoals(careDailyLocationId);

      // Exclude removed goals — bot sets removed_timestamp_ms when a goal is deleted
      const activeGoals = allGoals.filter(g => !g.removed_timestamp_ms);
      console.log(`📥 Retrieved ${activeGoals.length} active goals from CareDaily`);

      return activeGoals.map(g => this.mapCareDailyGoalToResidentGoal(
        g,
        residentId,
        facilityId,
        careDailyLocationId,
        resolveCategoryId(g.category)
      ));
    } catch (error) {
      console.warn('⚠️ CareDaily unreachable, falling back to Supabase for goals:', error);
      return this.getGoalsFromSupabase(residentId, facilityId);
    }
  }

  /**
   * Fetch goals from Supabase (unlinked residents, or fallback for linked residents).
   */
  private static async getGoalsFromSupabase(
    residentId: string,
    facilityId: string
  ): Promise<ResidentGoal[]> {
    const { data, error } = await supabase
      .from('resident_goals')
      .select('*')
      .eq('resident_id', residentId)
      .eq('facility_id', facilityId)
      .order('created_at', { ascending: false });

    if (error) {
      console.error('❌ Error fetching goals from Supabase:', error);
      throw error;
    }

    console.log(`✅ Retrieved ${data?.length || 0} goals from Supabase`);
    return (data || []).map(this.mapDbToGoal);
  }

  /**
   * Map a CareDaily goal to the ResidentGoal interface.
   * Uses goal_id as the stable id for linked goals.
   */
  private static mapCareDailyGoalToResidentGoal(
    g: CareDailyGoal,
    residentId: string,
    facilityId: string,
    careDailyLocationId: string,
    categoryId: string | null
  ): ResidentGoal {
    return {
      id: g.goal_id,
      residentId,
      facilityId,
      title: g.title,
      description: g.description,
      categoryId,
      completed: g.completed,
      completedAt: g.completed_timestamp_ms
        ? new Date(g.completed_timestamp_ms).toISOString()
        : null,
      createdAt: new Date(g.created_timestamp_ms).toISOString(),
      updatedAt: new Date(g.updated_timestamp_ms).toISOString(),
      careDailyGoalId: g.goal_id,
      careDailyLocationId,
    };
  }

  /**
   * Create a new goal for an unlinked resident (Supabase only).
   * Linked residents create goals via goalActivity (GoalsTracker → GoalActivityService).
   */
  static async createGoal(
    residentId: string,
    facilityId: string,
    goalInput: ResidentGoalInput
  ): Promise<ResidentGoal> {
    console.log('➕ Creating goal (local):', goalInput.title);

    const { data, error } = await supabase
      .from('resident_goals')
      .insert([{
        resident_id: residentId,
        facility_id: facilityId,
        caredaily_goal_id: null,
        caredaily_location_id: null,
        title: goalInput.title,
        description: goalInput.description,
        category_id: goalInput.categoryId,
        completed: false
      }])
      .select()
      .single();

    if (error) {
      console.error('❌ Error creating goal in Supabase:', error);
      throw error;
    }

    console.log('✅ Goal created in Supabase');
    return this.mapDbToGoal(data);
  }

  /**
   * Update an existing goal (Supabase only, unlinked residents).
   */
  static async updateGoal(
    goalId: string,
    updates: Partial<ResidentGoalInput>
  ): Promise<ResidentGoal> {
    console.log('📝 Updating goal (local):', goalId);

    const updateData: any = {};
    if (updates.title !== undefined) updateData.title = updates.title;
    if (updates.description !== undefined) updateData.description = updates.description;
    if (updates.categoryId !== undefined) updateData.category_id = updates.categoryId;

    const { data, error } = await supabase
      .from('resident_goals')
      .update(updateData)
      .eq('id', goalId)
      .select()
      .single();

    if (error) {
      console.error('❌ Error updating goal in Supabase:', error);
      throw error;
    }

    console.log('✅ Goal updated in Supabase');
    return this.mapDbToGoal(data);
  }

  /**
   * Toggle goal completion status (Supabase only, unlinked residents).
   * For linked residents the caller dispatches goalActivity (complete / reopen).
   */
  static async toggleGoalCompletion(
    goalId: string,
    completed: boolean
  ): Promise<ResidentGoal> {
    console.log(`${completed ? '✅' : '⏳'} Marking goal ${completed ? 'complete' : 'incomplete'} (local):`, goalId);

    const { data, error } = await supabase
      .from('resident_goals')
      .update({
        completed,
        completed_at: completed ? new Date().toISOString() : null
      })
      .eq('id', goalId)
      .select()
      .single();

    if (error) {
      console.error('❌ Error toggling goal completion in Supabase:', error);
      throw error;
    }

    console.log('✅ Goal completion updated in Supabase');
    return this.mapDbToGoal(data);
  }

  /**
   * Delete a goal from Supabase (unlinked residents only).
   * For linked residents the caller dispatches goalActivity "remove".
   */
  static async deleteGoal(goalId: string): Promise<void> {
    console.log('🗑️ Deleting goal (local):', goalId);

    const { error } = await supabase
      .from('resident_goals')
      .delete()
      .eq('id', goalId);

    if (error) {
      console.error('❌ Error deleting goal from Supabase:', error);
      throw error;
    }

    console.log('✅ Goal deleted from Supabase');
  }

  /**
   * Get goals summary statistics for a resident
   */
  static async getGoalsSummary(
    residentId: string,
    facilityId: string
  ): Promise<GoalsSummary> {
    console.log('📊 Fetching goals summary for resident:', residentId);

    const { data, error } = await supabase
      .rpc('get_resident_goals_summary', {
        p_resident_id: residentId,
        p_facility_id: facilityId
      })
      .single();

    if (error) {
      console.error('❌ Error fetching goals summary:', error);
      return {
        totalGoals: 0,
        completedGoals: 0,
        activeGoals: 0,
        completionPercentage: 0
      };
    }

    const summary = data as {
      total_goals: number;
      completed_goals: number;
      active_goals: number;
      completion_percentage: number;
    };

    return {
      totalGoals: summary.total_goals,
      completedGoals: summary.completed_goals,
      activeGoals: summary.active_goals,
      completionPercentage: summary.completion_percentage
    };
  }

  /**
   * Create multiple goals from templates (unlinked residents only).
   */
  static async createGoalsFromTemplates(
    residentId: string,
    facilityId: string,
    templates: { title: string; description?: string; categoryId?: string }[]
  ): Promise<ResidentGoal[]> {
    console.log('➕ Creating multiple goals from templates:', templates.length);

    const goalsData = templates.map(template => ({
      resident_id: residentId,
      facility_id: facilityId,
      title: template.title,
      description: template.description,
      category_id: template.categoryId,
      completed: false
    }));

    const { data, error } = await supabase
      .from('resident_goals')
      .insert(goalsData)
      .select();

    if (error) {
      console.error('❌ Error creating goals from templates:', error);
      throw error;
    }

    console.log('✅ Created goals successfully:', data?.length || 0);
    return (data || []).map(this.mapDbToGoal);
  }

  /**
   * Map a Supabase database row to ResidentGoal.
   */
  private static mapDbToGoal(row: any): ResidentGoal {
    return {
      id: row.id,
      residentId: row.resident_id,
      facilityId: row.facility_id,
      title: row.title,
      description: row.description,
      categoryId: row.category_id,
      completed: row.completed ?? false,
      completedAt: row.completed_at,
      createdAt: row.created_at,
      updatedAt: row.updated_at,
      careDailyGoalId: row.caredaily_goal_id,
      careDailyLocationId: row.caredaily_location_id
    };
  }
}
